import { useState, useEffect, useMemo } from 'react';
import { X, Minimize2, Maximize2, Lightbulb, AlertCircle, Info, Clock, Mic, MicOff, Pause, Play, PhoneOff, MessageSquare, User, FileText, Brain, BookOpen, MessageCircle, ArrowRightLeft } from 'lucide-react';
import { LiveTranscript } from './LiveTranscript';
import CustomerDetailsCard, { CustomerDetails } from './CustomerDetailsCard';
import { KnowledgeBasePanel } from './KnowledgeBasePanel';
import { TransferCallModal } from './TransferCallModal';
import { useTheme } from '@/contexts/ThemeContext';
import type { TranscriptChunk, CoachingSuggestion } from '@/lib/ringBuffer';

interface LiveCoachingPanelProps {
  isActive: boolean;
  isPostWork?: boolean;
  transcriptChunks: TranscriptChunk[];
  liveSuggestions: CoachingSuggestion[];
  isMuted?: boolean;
  isOnHold?: boolean;
  onClose?: () => void;
  onDismissSuggestion?: (id: string) => void;
  onToggleMute?: () => void;
  onToggleHold?: () => void;
  onHangUp?: () => void;
  onPostWorkComplete?: (outcome: 'positive' | 'negative' | 'neutral') => void;
  onRequestOutcomeSelection?: () => void;
  customerDetails?: {
    name?: string;
    phone?: string;
    company?: string;
    direction?: 'inbound' | 'outbound';
  };
  onSaveCustomerDetails?: (data: CustomerDetails) => void;
  callSid?: string;
  agentId?: string;
  onTransferCall?: (type: 'agent' | 'phone' | 'voicemail', target: string) => void;
}

// Priority scores for different suggestion types
const PRIORITY_SCORES = {
  critical: 100,
  warning: 75,
  suggestion: 50,
  info: 25
};

// Auto-dismiss timeouts (in ms) based on severity
const AUTO_DISMISS_TIMEOUTS = {
  critical: 0, // Never auto-dismiss
  warning: 60000, // 1 minute
  suggestion: 45000, // 45 seconds
  info: 30000 // 30 seconds
};

export function LiveCoachingPanel({
  isActive,
  isPostWork,
  transcriptChunks,
  liveSuggestions,
  isMuted,
  isOnHold,
  onClose,
  onDismissSuggestion,
  onToggleMute,
  onToggleHold,
  onHangUp,
  onPostWorkComplete,
  onRequestOutcomeSelection,
  customerDetails,
  onSaveCustomerDetails,
  callSid,
  agentId,
  onTransferCall
}: LiveCoachingPanelProps) {
  const { theme } = useTheme();
  const [isMinimized, setIsMinimized] = useState(false);
  const [dismissedSuggestions, setDismissedSuggestions] = useState<Set<string>>(new Set());
  const [callDuration, setCallDuration] = useState(0);
  const [callStartTime, setCallStartTime] = useState<number | null>(null);
  const [showTranscript, setShowTranscript] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'customer' | 'service' | 'coaching' | 'knowledge' | 'transcript'>('customer');
  const [showOutcomeModal, setShowOutcomeModal] = useState(false);

  // Start call timer when panel becomes active
  useEffect(() => {
    if (isActive && !callStartTime) {
      setCallStartTime(Date.now());
      setCallDuration(0);
    } else if (!isActive) {
      setCallStartTime(null);
    }
  }, [isActive]);

  // Update call duration every second
  useEffect(() => {
    if (!callStartTime) return;

    const timer = setInterval(() => {
      setCallDuration(Math.floor((Date.now() - callStartTime) / 1000));
    }, 1000);

    return () => clearInterval(timer);
  }, [callStartTime]);

  // Auto-dismiss suggestions based on severity
  useEffect(() => {
    const timers: number[] = [];

    liveSuggestions.forEach(suggestion => {
      const timeout = AUTO_DISMISS_TIMEOUTS[suggestion.severity];
      if (timeout > 0 && !dismissedSuggestions.has(suggestion.id)) {
        const timer = window.setTimeout(() => {
          setDismissedSuggestions(prev => new Set([...prev, suggestion.id]));
          onDismissSuggestion?.(suggestion.id);
        }, timeout);
        timers.push(timer);
      }
    });

    return () => timers.forEach(timer => clearTimeout(timer));
  }, [liveSuggestions, dismissedSuggestions, onDismissSuggestion]);

  // Sort and filter suggestions by priority
  const visibleSuggestions = useMemo(() => {
    return liveSuggestions
      .filter(s => !dismissedSuggestions.has(s.id))
      .sort((a, b) => {
        // First by priority score
        const priorityDiff = (PRIORITY_SCORES[b.severity] || 0) - (PRIORITY_SCORES[a.severity] || 0);
        if (priorityDiff !== 0) return priorityDiff;
        
        // Then by timestamp (newer first)
        return b.timestamp - a.timestamp;
      });
  }, [liveSuggestions, dismissedSuggestions]);

  // Calculate average response times
  const metrics = useMemo(() => {
    const claudeSuggestions = visibleSuggestions.filter(s => s.source === 'claude' && s.responseTime);
    const openaiSuggestions = visibleSuggestions.filter(s => s.source === 'openai' && s.responseTime);
    
    const claudeAvg = claudeSuggestions.length > 0
      ? Math.round(claudeSuggestions.reduce((sum, s) => sum + (s.responseTime || 0), 0) / claudeSuggestions.length)
      : null;
    
    const openaiAvg = openaiSuggestions.length > 0
      ? Math.round(openaiSuggestions.reduce((sum, s) => sum + (s.responseTime || 0), 0) / openaiSuggestions.length)
      : null;

    // Calculate counts by severity
    const counts = {
      critical: visibleSuggestions.filter(s => s.severity === 'critical').length,
      warning: visibleSuggestions.filter(s => s.severity === 'warning').length,
      info: visibleSuggestions.filter(s => s.severity === 'suggestion' || s.severity === 'info').length,
      claude: visibleSuggestions.filter(s => s.source === 'claude').length,
      openai: visibleSuggestions.filter(s => s.source === 'openai').length
    };

    return {
      claudeAvg,
      openaiAvg,
      counts
    };
  }, [visibleSuggestions]);

  // Format call duration as MM:SS
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <AlertCircle className="w-4 h-4 text-red-400" />;
      case 'warning':
        return <AlertCircle className="w-4 h-4 text-yellow-400" />;
      case 'suggestion':
        return <Lightbulb className="w-4 h-4 text-blue-400" />;
      default:
        return <Info className="w-4 h-4 text-slate-400" />;
    }
  };

  const getSeverityColors = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-900/30 border-red-500';
      case 'warning':
        return 'bg-yellow-900/30 border-yellow-500';
      case 'suggestion':
        return 'bg-blue-900/30 border-blue-500';
      default:
        return 'bg-slate-800 border-slate-600';
    }
  };

  const getSourceBadge = (source?: string) => {
    switch (source) {
      case 'claude':
        return <span className="px-2 py-0.5 bg-purple-600/30 text-purple-300 border border-purple-500/50 rounded text-xs font-medium">Claude</span>;
      case 'openai':
        return <span className="px-2 py-0.5 bg-green-600/30 text-green-300 border border-green-500/50 rounded text-xs font-medium">OpenAI</span>;
      case 'heuristic':
        return <span className="px-2 py-0.5 bg-gray-600/30 text-gray-300 border border-gray-500/50 rounded text-xs font-medium">Auto</span>;
      default:
        return null;
    }
  };

  // Count suggestions by type for badges
  const coachingCount = visibleSuggestions.length;
  const criticalCount = visibleSuggestions.filter(s => s.severity === 'critical').length;

  const handleDismiss = (id: string) => {
    setDismissedSuggestions(prev => new Set([...prev, id]));
    onDismissSuggestion?.(id);
  };

  // Derive call context from transcripts for knowledge base
  const callContext = useMemo(() => {
    const recentTranscripts = transcriptChunks.slice(-10);
    const transcriptText = recentTranscripts.map(t => t.text).join(' ').toLowerCase();
    
    // Extract potential objections or scenarios
    const hasObjection = /but|however|concern|worried|expensive|cost|price|can't afford/.test(transcriptText);
    const hasPricing = /price|cost|how much|payment|quote|estimate/.test(transcriptText);
    const hasService = /repair|fix|service|maintenance|install|replacement/.test(transcriptText);
    const hasEmergency = /emergency|urgent|asap|right away|immediately/.test(transcriptText);
    
    return {
      scenario: hasObjection ? 'objection_handling' : 
                hasEmergency ? 'emergency_call' :
                hasPricing ? 'pricing_inquiry' : undefined,
      customerObjection: hasObjection ? transcriptText.slice(-200) : undefined,
      serviceType: hasService ? 'hvac_service' : undefined,
      agentSentiment: undefined
    };
  }, [transcriptChunks]);

  if (!isActive) return null;

  return (
    <>
      <div
        className={`fixed top-20 bottom-0 shadow-2xl transition-all duration-300 z-40 flex flex-col ${
          isMinimized ? 'right-0 w-16 left-auto' : 'left-[280px] right-0'
        }`}
        style={{
          backgroundColor: theme.colors.bgPage,
          borderLeft: `1px solid ${theme.colors.borderColor}`
        }}
      >
      {/* Header - Single row with all controls */}
      <div style={{
        borderBottom: `1px solid ${theme.colors.borderColor}`,
        backgroundColor: theme.colors.bgCard
      }}>
        <div className="flex items-center justify-between px-4 py-3 gap-4">
          {/* Left side - Call Time and Recording Indicator */}
          {!isMinimized && (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4" style={{ color: theme.colors.infoText }} />
                <span style={{ color: theme.colors.textSecondary }}>Call Time:</span>
                <span className="font-mono font-semibold" style={{ color: theme.colors.infoText }}>
                  {formatDuration(callDuration)}
                </span>
              </div>
              
              {/* Recording Indicator */}
              <div className="flex items-center gap-2 text-xs">
                <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
                <span style={{ color: theme.colors.textSecondary }}>REC</span>
              </div>
            </div>
          )}

          {/* Center - Call Control Buttons or Post-Work Button */}
          {!isMinimized && (
            <div className="flex gap-2">
              {isPostWork ? (
                <button
                  onClick={() => {
                    // Request outcome selection from parent (AdminLayout)
                    onRequestOutcomeSelection?.();
                    setShowOutcomeModal(true);
                  }}
                  className="px-4 py-2 rounded font-semibold text-white transition-colors shadow-lg"
                  style={{ backgroundColor: '#10b981' }}
                  title="Complete post-work and submit call outcome"
                >
                  ✓ Post Work Completed
                </button>
              ) : (
                <>
                  <button
                    onClick={onToggleMute}
                    className="p-2 rounded text-white transition-colors"
                    style={{ backgroundColor: isMuted ? '#ef4444' : '#6b7280' }}
                    title={isMuted ? 'Unmute' : 'Mute'}
                  >
                    {isMuted ? <MicOff size={16} /> : <Mic size={16} />}
                  </button>
                  <button
                    onClick={onToggleHold}
                    className="p-2 rounded text-white transition-colors"
                    style={{ backgroundColor: isOnHold ? '#f59e0b' : '#6b7280' }}
                    title={isOnHold ? 'Resume' : 'Hold'}
                  >
                    {isOnHold ? <Play size={16} /> : <Pause size={16} />}
                  </button>
                  <button
                    onClick={() => setShowTransferModal(true)}
                    className="p-2 rounded text-white transition-colors"
                    style={{ backgroundColor: '#3b82f6' }}
                    title="Transfer Call"
                  >
                    <ArrowRightLeft size={16} />
                  </button>
                  <button
                    onClick={onHangUp}
                    className="p-2 rounded text-white transition-colors"
                    style={{ backgroundColor: '#ef4444' }}
                    title="Hang Up"
                  >
                    <PhoneOff size={16} />
                  </button>
                </>
              )}
            </div>
          )}

          {/* Right side - Transcript Toggle and Window Controls */}
          <div className="flex items-center gap-2">
            {!isMinimized && (
              <button
                onClick={() => setShowTranscript(!showTranscript)}
                className="p-2 rounded transition-colors"
                style={{
                  backgroundColor: showTranscript ? '#3b82f6' : 'transparent',
                  color: showTranscript ? '#ffffff' : theme.colors.textSecondary
                }}
                onMouseEnter={(e) => {
                  if (!showTranscript) e.currentTarget.style.backgroundColor = theme.colors.bgHover;
                }}
                onMouseLeave={(e) => {
                  if (!showTranscript) e.currentTarget.style.backgroundColor = 'transparent';
                }}
                title={showTranscript ? 'Hide Transcript' : 'Show Transcript'}
              >
                <MessageSquare className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="p-1.5 rounded transition-colors"
              style={{
                color: theme.colors.textSecondary
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.bgHover}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              title={isMinimized ? 'Expand' : 'Minimize'}
            >
              {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
            </button>
            {onClose && (
              <button
                onClick={onClose}
                className="p-1.5 rounded transition-colors"
                style={{
                  color: theme.colors.textSecondary
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.bgHover}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                title="Close"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {!isMinimized && (
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Critical AI Alert - Always visible at top if exists */}
          {visibleSuggestions.length > 0 && visibleSuggestions[0].severity === 'critical' && (
            <div className="px-3 py-2 flex items-center gap-2" style={{
              backgroundColor: '#7f1d1d',
              borderBottom: `2px solid ${theme.colors.borderColor}`
            }}>
              <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <span className="text-xs font-bold text-white">� {visibleSuggestions[0].title}: </span>
                <span className="text-xs text-gray-200">{visibleSuggestions[0].message}</span>
              </div>
              <button onClick={() => handleDismiss(visibleSuggestions[0].id)} className="p-1 text-white hover:bg-red-900 rounded" title="Dismiss">
                <X className="w-3 h-3" />
              </button>
            </div>
          )}

          {/* Tab Navigation */}
          <div className="flex border-b" style={{ borderColor: theme.colors.borderColor, backgroundColor: theme.colors.bgCard }}>
            <button
              onClick={() => setActiveTab('customer')}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                activeTab === 'customer' ? 'border-blue-500' : 'border-transparent'
              }`}
              style={{
                color: activeTab === 'customer' ? theme.colors.infoText : theme.colors.textSecondary,
                backgroundColor: activeTab === 'customer' ? theme.colors.bgHover : 'transparent'
              }}
            >
              <User className="w-4 h-4" />
              <span>Customer</span>
            </button>

            <button
              onClick={() => setActiveTab('service')}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                activeTab === 'service' ? 'border-blue-500' : 'border-transparent'
              }`}
              style={{
                color: activeTab === 'service' ? theme.colors.infoText : theme.colors.textSecondary,
                backgroundColor: activeTab === 'service' ? theme.colors.bgHover : 'transparent'
              }}
            >
              <FileText className="w-4 h-4" />
              <span>Service</span>
            </button>

            <button
              onClick={() => setActiveTab('coaching')}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                activeTab === 'coaching' ? 'border-blue-500' : 'border-transparent'
              }`}
              style={{
                color: activeTab === 'coaching' ? theme.colors.infoText : theme.colors.textSecondary,
                backgroundColor: activeTab === 'coaching' ? theme.colors.bgHover : 'transparent'
              }}
            >
              <Brain className="w-4 h-4" />
              <span>Coaching</span>
              {coachingCount > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${
                  criticalCount > 0 ? 'bg-red-500 text-white' : 'bg-blue-500 text-white'
                }`}>
                  {coachingCount}
                </span>
              )}
            </button>

            <button
              onClick={() => setActiveTab('knowledge')}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                activeTab === 'knowledge' ? 'border-blue-500' : 'border-transparent'
              }`}
              style={{
                color: activeTab === 'knowledge' ? theme.colors.infoText : theme.colors.textSecondary,
                backgroundColor: activeTab === 'knowledge' ? theme.colors.bgHover : 'transparent'
              }}
            >
              <BookOpen className="w-4 h-4" />
              <span>Knowledge</span>
            </button>

            <button
              onClick={() => setActiveTab('transcript')}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                activeTab === 'transcript' ? 'border-blue-500' : 'border-transparent'
              }`}
              style={{
                color: activeTab === 'transcript' ? theme.colors.infoText : theme.colors.textSecondary,
                backgroundColor: activeTab === 'transcript' ? theme.colors.bgHover : 'transparent'
              }}
            >
              <MessageCircle className="w-4 h-4" />
              <span>Transcript</span>
            </button>
          </div>

          {/* Tab Content - Fixed height, no scrolling at root */}
          <div className="flex-1 overflow-hidden">
            {activeTab === 'customer' && (
              <div className="h-full overflow-auto p-4">
                <CustomerDetailsCard
                  phone={customerDetails?.phone}
                  direction={customerDetails?.direction}
                  transcriptChunks={transcriptChunks}
                  onSave={onSaveCustomerDetails}
                  compactMode={true}
                />
              </div>
            )}

            {activeTab === 'service' && (
              <div className="h-full overflow-auto p-4">
                <CustomerDetailsCard
                  phone={customerDetails?.phone}
                  direction={customerDetails?.direction}
                  transcriptChunks={transcriptChunks}
                  onSave={onSaveCustomerDetails}
                  compactMode={true}
                  serviceNotesOnly={true}
                />
              </div>
            )}

            {activeTab === 'coaching' && (
              <div className="h-full overflow-auto p-4">
                {visibleSuggestions.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center p-8">
                    <Lightbulb className="w-16 h-16 mb-4 opacity-20" style={{ color: theme.colors.textTertiary }} />
                    <p className="text-lg font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
                      No coaching suggestions yet
                    </p>
                    <p className="text-sm" style={{ color: theme.colors.textTertiary }}>
                      AI will provide real-time guidance as the conversation progresses
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {visibleSuggestions.map(suggestion => (
                      <div
                        key={suggestion.id}
                        className={`rounded-lg border p-3 ${getSeverityColors(suggestion.severity)}`}
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            {getSeverityIcon(suggestion.severity)}
                            <h4 className="font-semibold text-sm truncate" style={{ color: theme.colors.textPrimary }}>
                              {suggestion.title}
                            </h4>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {getSourceBadge(suggestion.source)}
                            <button
                              onClick={() => handleDismiss(suggestion.id)}
                              className="p-1 rounded transition-colors hover:bg-white/10"
                              title="Dismiss"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <p className="text-sm mb-2" style={{ color: theme.colors.textSecondary }}>
                          {suggestion.message}
                        </p>
                        {suggestion.responseTime && (
                          <div className="mt-2 text-xs opacity-50" style={{ color: theme.colors.textTertiary }}>
                            Response time: {suggestion.responseTime}ms
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'knowledge' && (
              <div className="h-full overflow-hidden">
                <KnowledgeBasePanel
                  callContext={callContext}
                />
              </div>
            )}

            {activeTab === 'transcript' && (
              <div className="h-full overflow-hidden">
                <LiveTranscript
                  chunks={transcriptChunks}
                  isActive={isActive}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>

    {/* Transfer Call Modal */}
    {callSid && agentId && (
      <TransferCallModal
        isOpen={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        callSid={callSid}
        fromAgentId={agentId}
        onTransfer={(type, target) => {
          console.log(`[LiveCoachingPanel] Transfer requested: ${type} -> ${target}`);
          if (onTransferCall) {
            onTransferCall(type, target);
          }
          setShowTransferModal(false);
        }}
      />
    )}

    {/* Post-Work Modal - Call Outcome Selection (shown after "Post Work Completed" is clicked) */}
    {showOutcomeModal && onPostWorkComplete && (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
        <div 
          className="rounded-lg p-8 w-full max-w-md shadow-2xl"
          style={{ 
            backgroundColor: theme.colors.bgCard,
            border: `2px solid ${theme.colors.borderColor}`
          }}
        >
          <h3 className="text-xl font-bold mb-4 text-center" style={{ color: theme.colors.textPrimary }}>
            How did the call go?
          </h3>
          <p className="text-sm mb-6 text-center" style={{ color: theme.colors.textSecondary }}>
            Select the outcome of this call
          </p>
          
          <div className="flex flex-col gap-3">
            <button
              onClick={async () => {
                console.log('[LiveCoachingPanel] Positive outcome clicked');
                await onPostWorkComplete('positive');
                console.log('[LiveCoachingPanel] onPostWorkComplete finished, closing modal');
                setShowOutcomeModal(false);
              }}
              className="px-6 py-4 rounded-lg font-semibold text-white transition-all transform hover:scale-105"
              style={{ backgroundColor: '#10b981' }}
            >
              😊 Positive
              <div className="text-xs font-normal mt-1 opacity-80">
                Customer was satisfied, issue resolved
              </div>
            </button>
            
            <button
              onClick={async () => {
                console.log('[LiveCoachingPanel] Neutral outcome clicked');
                await onPostWorkComplete('neutral');
                console.log('[LiveCoachingPanel] onPostWorkComplete finished, closing modal');
                setShowOutcomeModal(false);
              }}
              className="px-6 py-4 rounded-lg font-semibold text-white transition-all transform hover:scale-105"
              style={{ backgroundColor: '#6b7280' }}
            >
              😐 Neutral
              <div className="text-xs font-normal mt-1 opacity-80">
                Call completed, no strong sentiment
              </div>
            </button>
            
            <button
              onClick={async () => {
                console.log('[LiveCoachingPanel] Negative outcome clicked');
                await onPostWorkComplete('negative');
                console.log('[LiveCoachingPanel] onPostWorkComplete finished, closing modal');
                setShowOutcomeModal(false);
              }}
              className="px-6 py-4 rounded-lg font-semibold text-white transition-all transform hover:scale-105"
              style={{ backgroundColor: '#ef4444' }}
            >
              😞 Negative
              <div className="text-xs font-normal mt-1 opacity-80">
                Customer unhappy, issue unresolved
              </div>
            </button>
          </div>

          <p className="text-xs mt-4 text-center" style={{ color: theme.colors.textTertiary }}>
            This helps improve AI coaching and call analytics
          </p>
        </div>
      </div>
    )}
  </>
  );
}
